import autobind from "autobind-decorator";
import * as API from "../../libs/types/api";
import throttle from "lodash/throttle";
import { computed, observable, action } from "mobx";
import { RouterState, RouterStore} from "../../libs/ssr/src/router/store";
import { IntlStore } from "../../libs/intl/src/store";
import { ThemeStore } from "./stores/theme";
import { BillingStore } from "./stores/billing";
import { CoreUtils } from "../../libs/common/utils/core";
import { logger } from "../../libs/common/utils/logger";
import { ServiceStore } from "./stores/service";
import { NotificationsStore } from "./stores/notifications";
import { APIStore } from "./stores/api";
import {routeIsMatch} from "../routes";
import {generateStaffRestrictions} from "../react/ui/dashboard/views/staff";
import {config} from "../config";
import {GenericListBoardCollection} from "../../libs/components/libs/listboard/types";
import {OrderUtils} from "../../libs/common/utils/order";
import localStore from "store";
import {cloneDeepSafe} from "../../libs/common/utils/mobx";
import {PaymentMethods} from "../../libs/common/data/payment_methods";

interface GenericQueryCollection<T> {
  items: T[];
  count: number;
  page: number;
}
interface GenericAsyncListCollection<T> {
  loading: boolean;
  error: string;
  items: T[];
}

type RestaurantsStore = GenericAsyncListCollection<API.DashboardRestaurantsResponseItem>;
type StaffStore = GenericAsyncListCollection<UserSchema>;
type APISStore = GenericAsyncListCollection<APISchema>;

interface OrdersView {
  layout: 0 | 1;
  boardSize: 2 | 3 | 4 | 5;
  hideUnconfirmed: boolean;
}

@autobind
export class RootStore {

  theme: ThemeStore;
  router: RouterStore;
  intl: IntlStore;
  api: APIStore;
  service: ServiceStore;
  notifications: NotificationsStore;
  billing: BillingStore;

  routeChangeCount: number = 0;

  @observable auth: AuthState;
  @observable loader: LoaderState;
  @observable view: ViewState;
  @observable ably: AblyState;

  @observable reseller: ResellerSchema | null;
  @observable organisation: OrganisationSchema | null;
  @observable website: WebsiteSchema | null;
  @observable restaurants: RestaurantsStore;
  @observable staff: StaffStore;
  @observable apis: APISStore;

  @observable restaurant: RestaurantSchema | null;

  @observable customers: GenericQueryCollection<CustomerSchema>;
  @observable customer: CustomerSchema | null;

  @observable ordersView: OrdersView;
  @observable ordersBoard: GenericListBoardCollection<OrderSchema>;
  @observable orders: GenericQueryCollection<OrderSchema>;
  @observable order: OrderSchema | null;

  @observable bookings: GenericQueryCollection<BookingSchema>;
  @observable booking: BookingSchema | null;

  // simply pass a parsed serialized state, the reason it's not auto parsed is to enable type safe construction if needed
  constructor() {

    this.auth = {
      type: null,
      item: null,
      token: null,
      decoded: null,
      fetching: false,
      error: null,
    };

    this.view = {
      breakpoint: "md",
      screen_width: 720,
      scroll_top: 0,
      sidenav_active: false,
    };

    this.loader = {
      active: true,
      opacity: 1,
      title: "Loading...",
      message: "This can take up to one minute the first time or if an update has been released",
    };

    this.ably = {
      status: "disconnected",
      connected_once: false,
      printers: [],
    };

    this.reseller = null;
    this.organisation = null;
    this.website = null;
    this.restaurants = observable({
      loading: false,
      error: "",
      items: [],
    });
    this.staff = observable({
      loading: false,
      error: "",
      items: [],
    });
    this.apis = observable({
      loading: false,
      error: "",
      items: [],
    });

    this.restaurant = null;

    this.customers = {
      items: [],
      count: 0,
      page: 0,
    };
    this.customer = null;

    const orderViewSettings = localStore.get("store-ordersView") || {};
    this.ordersView = {
      layout: orderViewSettings.layout ? parseInt(orderViewSettings.layout, 10) as 0 | 1 : 0,
      boardSize: orderViewSettings.boardSize ? parseInt(orderViewSettings.boardSize, 10) as 2 | 3 | 4 | 5 : 3,
      hideUnconfirmed: orderViewSettings.hideUnconfirmed !== undefined ? orderViewSettings.hideUnconfirmed : false,
    };
    this.ordersBoard = {
      loading: false,
      error: "",
      lists: {},
    };
    this.orders = {
      items: [],
      count: 0,
      page: 0,
    };
    this.order = null;

    this.bookings = {
      items: [],
      count: 0,
      page: 0,
    };
    this.booking = null;

    this.theme = new ThemeStore(this);
    this.router = new RouterStore(undefined, this.routeOnChange);
    // this.intl = new IntlStore(this);

    this.intl = new IntlStore({
      useReactModule: true,
    });

    this.notifications = new NotificationsStore(this);
    this.service = new ServiceStore(this);
    this.billing = new BillingStore(this);
    this.api = new APIStore(this, {
      auth_token_error: this.service.handle_auth_token_error,
    });

    this.routeOnChange(this.router.s);
    this.windowResize();
    this.windowScroll();
    window.addEventListener("resize", throttle(this.windowResize, 100));
    document.addEventListener("scroll", throttle(this.windowScroll, 50));
  }

  // LOGIN
  @action routeOnChange = (s: RouterState) => {
    try {

      const { restrictions, auth } = this;

      // INC
      this.routeChangeCount++;

      window.Intercom("update");

      // GET ROUTE
      const route = routeIsMatch(s.path);

      if (!route) { // NOT FOUND
        this.router.set404(true);
        document.title = "404 - Not Found";
      }
      else { // FOUND

        this.router.set404(false);

        document.title = route.title;

        if (this.routeChangeCount > 1 && route.auth && !auth.token) { // NOT AUTHENTICATED
          logger.info("ROUTE TAKE TO LOGIN");
          this.router.push("/login");
        }
        else if (
          auth.item &&
          auth.item.type === "staff" &&
          route.match &&
          route.match.rid &&
          restrictions.restaurants.indexOf(route.match.rid) === -1
        ) {
          logger.info("ROUTE TAKE TO / RID");
          this.router.push("/");
        }
        else if (route.restriction_keys) {

          logger.info("ROUTE RESTRICTIONS");

          let is_restricted = true;

          for (const restriction_key of route.restriction_keys) {
            let available;
            const access_keys = restriction_key.split(".");
            if (access_keys.length === 3) {
              // @ts-ignore
              available = restrictions[access_keys[0]][access_keys[1]][access_keys[2]];
            }
            else {
              // @ts-ignore
              available = restrictions[access_keys[0]][access_keys[1]];
            }
            if (available) {
              is_restricted = false;
              break;
            }
          }

          if (is_restricted) {
            logger.info("ROUTE RESTRICTIONS TAKE TO /");
            this.router.push("/");
          }

        }

        // SCROLL TOP
        const sr = document.getElementById("scroll-root");
        const noScrollPages = ["restaurant_bookings", "restaurant_orders", "restaurant_customers"];
        if (sr && sr.scroll && noScrollPages.indexOf(route.key) === -1) {
          sr.scroll({ top: 0, left: 0, behavior: "auto" });
        }

      }

    }
    catch (err) {
      logger.captureException(err, "ROUTE CHANGE ERROR");
    }
  }

  @computed get showMainUserSupport() {
    const user = this.auth.item;
    return this.isMainReseller && user && user.type !== "staff";
  }

  @computed get showMainSupport() {
    return this.isMainReseller;
  }

  // COMPUTED
  @computed get isMainReseller() {
    if (this.reseller) {
      return ["cloudwaitress", "cloudwaitress-test"].indexOf(this.reseller._id) !== -1;
    }
    return false;
  }

  @computed get isStaff() {
    if (this.auth.item) {
      return this.auth.item.type === "staff";
    }
    return false;
  }

  @computed get restrictions() {

    let re;
    if (this.auth.item) {
      re = cloneDeepSafe(this.auth.item.restrictions) || generateStaffRestrictions();
    }
    else {
      re = generateStaffRestrictions();
    }

    const rr = re.restaurant;

    let restaurantSettingsEnabled = false;
    if (re.restaurant.settings_detail) {
      const sd = re.restaurant.settings_detail;
      if (sd.system || sd.services || sd.payments || sd.website || sd.integrations) {
        restaurantSettingsEnabled = true;
      }
    }
    else if (re.restaurant.settings) {
      restaurantSettingsEnabled = true;
    }

    const restaurantOrderViews: string[] = [];
    if (typeof re.restaurant.orders_list === "undefined") {
      if (re.restaurant.orders) {
        restaurantOrderViews.push("board");
        restaurantOrderViews.push("list");
      }
    }
    else {
      if (re.restaurant.orders_board)
        restaurantOrderViews.push("board");
      if (re.restaurant.orders_list)
        restaurantOrderViews.push("list");
    }

    const restaurantView = rr.dashboard || restaurantOrderViews.length > 0 || rr.bookings || rr.menus || rr.customers || restaurantSettingsEnabled;

    const restaurantNotificationsEnabled = restaurantOrderViews.length > 0 || rr.bookings || rr.customers || restaurantSettingsEnabled;

    return {
      ...re,
      _: {
        restaurantView,
        restaurantSettingsEnabled,
        restaurantOrderViews,
        restaurantNotificationsEnabled,
      },
    };

  }

  @computed get trialExpiry() {
    const r = this.reseller!;
    const organisation = this.organisation;
    if (organisation && r.chargebee && r.chargebee.subscription.trial_period_days) {
      return organisation.created + (1000 * 60 * 60 * 24 * r.chargebee.subscription.trial_period_days);
    }
    return 0;
  }

  @computed get trialExpired() {
    return Date.now() > this.trialExpiry; // 30 DAYS
  }

  @computed get isMapped() {
    const r = this.restaurant;
    if (!r) return false;
    return r.location.map_data.type === "google_maps" || r.location.map_data.type === "osm";
  }

  @computed get storeURL() {
    const reseller = this.reseller;
    const r = this.restaurant;
    if (!r) {
      return "";
    }
    if (config.isTest) {
      return "http://localhost:3000";
    }
    return r.domain ? `https://${r.domain}` : `https://${r.subdomain}.${reseller!.store_host}`;
  }

  // UTILS
  getPaymentMethodName = (method: string) => {

    const r = this.restaurant;

    const isBaseMethod = PaymentMethods.indexOf(method) !== -1;
    let paymentName = this.intl.i18n.t(`constants.payment.backend_method.${method}`);

    if (!r) {
      return isBaseMethod ? paymentName : method;
    }

    const paymentMethod = r.settings.payments[method];
    if (!isBaseMethod) {
      if (paymentMethod) {
        paymentName = paymentMethod.label || method;
      }
      else {
        paymentName = method;
      }
    }

    return paymentName;

  }

  // UPDATERS
  @action setAuth = (data: AuthState) => {
    this.auth = data;
  }
  @action updateAuth = (data: Partial<AuthState>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof AuthState];
        if (value !== undefined) {
          this.auth[key as keyof AuthState] = value;
        }
      }
    }
  }

  @action setView = (data: ViewState) => {
    this.view = data;
  }
  @action updateView = (data: Partial<ViewState>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof ViewState];
        if (value !== undefined) {
          this.view[key as keyof ViewState] = value;
        }
      }
    }
  }

  @action updateAbly = (data: Partial<AblyState>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof AblyState];
        if (value !== undefined) {
          this.ably[key as keyof AblyState] = value;
        }
      }
    }
  }

  @action setLoader = (data: LoaderState) => {
    this.loader = data;
  }
  @action updateLoader = (data: Partial<LoaderState>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof LoaderState];
        if (value !== undefined) {
          this.loader[key as keyof LoaderState] = value;
        }
      }
    }
  }
  @action toggleLoader = (data: boolean) => {
    this.loader.active = data;
  }

  @action setOrganisation = (obj: OrganisationSchema | null) => {
    this.organisation = obj;
  }
  @action updateOrganisation = (data: Partial<OrganisationSchema>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof OrganisationSchema];
        if (value !== undefined && this.organisation) {
          this.organisation[key as keyof OrganisationSchema] = value;
        }
      }
    }
  }

  @action setWebsite = (obj: WebsiteSchema | null) => {
    this.website = obj;
  }
  @action updateWebsite = (data: Partial<WebsiteSchema>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof WebsiteSchema];
        if (value !== undefined && this.website) {
          this.website[key as keyof WebsiteSchema] = value;
        }
      }
    }
  }

  @action setRestaurant = (r: RestaurantSchema | null) => {
    this.restaurant = r;
    if (r) {
      this.intl.set({
        lng: "en",
        currency: {
          ...r.settings.region.currency,
          step: CoreUtils.currency.precision_to_step(r.settings.region.currency.precision),
        },
        tz: r.settings.region.timezone,
        locale: r.settings.region.locale,
        formats: r.settings.region.formats,
      });
    }
  }
  @action updateRestaurant = (data: Partial<RestaurantSchema>) => {

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof RestaurantSchema];
        if (value !== undefined && this.restaurant) {
          this.restaurant[key as keyof RestaurantSchema] = value;
        }
      }
    }

    if (data && data.settings && data.settings.region) {
      if (data.settings.region.timezone) {
        this.intl.update({ tz: data.settings.region.timezone });
      }
      if (data.settings.region.locale) {
        this.intl.update({ locale: data.settings.region.locale });
      }
      if (data.settings.region.currency) {
        this.intl.update({
          currency: {
            ...data.settings.region.currency,
            step: CoreUtils.currency.precision_to_step(data.settings.region.currency.precision),
          },
        });
      }
      if (data.settings.region.formats && data.settings.region.currency) {
        this.intl.update({
          formats: data.settings.region.formats,
        });
      }
    }

  }
  @action updateRestaurants = (data: Partial<RestaurantsStore>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof RestaurantsStore];
        if (value !== undefined && this.restaurants) {
          this.restaurants[key as keyof RestaurantsStore] = value;
        }
      }
    }
  }
  @action updateRestaurantComplete = (_id: string, update: Partial<RestaurantSchema>) => {

    // UPDATE SINGLE
    if (this.restaurant && this.restaurant._id === _id) {
      this.updateRestaurant(update);
    }

    // UPDATE COLLECTION
    const items = [ ...this.restaurants.items ];
    for (const [i, o] of items.entries()) {
      if (o._id === _id) {
        items[i] = { ...items[i], ...update };
        this.updateRestaurants({ items });
        break;
      }
    }

  }

  @action updateStaff = (data: Partial<StaffStore>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof StaffStore];
        if (value !== undefined && this.staff) {
          this.staff[key as keyof StaffStore] = value;
        }
      }
    }
  }
  @action updateApis = (data: Partial<APISStore>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof APISStore];
        if (value !== undefined && this.apis) {
          this.apis[key as keyof APISStore] = value;
        }
      }
    }
  }

  @action setReseller = (data: ResellerSchema | null) => {
    this.reseller = data;
  }
  @action updateReseller = (data: Partial<ResellerSchema>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof ResellerSchema];
        if (value !== undefined && this.reseller) {
          this.reseller[key as keyof ResellerSchema] = value;
        }
      }
    }
  }

  @action setOrder = (data: OrderSchema | null) => {
    this.order = data;
  }
  @action updateOrder = (data: Partial<OrderSchema>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof OrderSchema];
        if (value !== undefined && this.order) {
          this.order[key as keyof OrderSchema] = value;
        }
      }
    }
  }
  @action updateOrders = (data: Partial<GenericQueryCollection<OrderSchema>>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof GenericQueryCollection<OrderSchema>];
        if (value !== undefined && this.orders) {
          this.orders[key as keyof GenericQueryCollection<OrderSchema>] = value;
        }
      }
    }
  }
  @action updateOrdersBoard = (data: Partial<GenericListBoardCollection<OrderSchema>>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof GenericListBoardCollection<OrderSchema>];
        if (value !== undefined && this.ordersBoard) {
          this.ordersBoard[key as keyof GenericListBoardCollection<OrderSchema>] = value;
        }
      }
    }
  }
  @action updateOrderComplete = (order: OrderSchema) => {

    const r = this.restaurant;

    if (r) {

      const tz = r.settings.region.timezone;

      // UPDATE SINGLE
      if (this.order && this.order._id === order._id) {
        this.order = order;
      }

      // UPDATE COLLECTION
      if (this.orders.items.length > 0) {
        for (const [i, o] of this.orders.items.entries()) {
          if (o._id === order._id) {
            this.orders.items[i] = order;
            break;
          }
        }
      }

      // UPDATE LISTBOARD
      const nextListId = OrderUtils.getOrderManagementStatus(order, tz);

      let listId = "";
      for (const key in this.ordersBoard.lists) {
        if (this.ordersBoard.lists[key]) {
          let index = -1;
          index = this.ordersBoard.lists[key].items.findIndex((o) => o._id === order._id);
          if (index !== -1) {
            listId = key;
            break;
          }
        }
      }

      if (listId) {

        // SPLICE
        const itemIndex = this.ordersBoard.lists[listId].items.findIndex((o) => o._id === order._id);
        this.ordersBoard.lists[listId].items.splice(itemIndex, 1);

        // PUSH
        if (nextListId === "complete" || nextListId === "cancelled") {
          if (this.ordersBoard.lists[nextListId].items.length >= 5) {
            this.ordersBoard.lists[nextListId].items.pop();
          }
          this.ordersBoard.lists[nextListId].items.unshift(order);
        }
        else {
          this.ordersBoard.lists[nextListId].items.push(order);
          // tslint:disable-next-line
          this.ordersBoard.lists[nextListId].items = this.ordersBoard.lists[nextListId].items.slice().sort(OrderUtils.sortFunctionByStatus(nextListId, tz));
        }

      }

    }

  }
  @action removeOrder = (_id: string) => {
    // UPDATE SINGLE
    if (this.order && this.order._id === _id) {
      this.setOrder(null);
    }
    // UPDATE COLLECTION
    const orders = [ ...this.orders.items ];
    for (const [i, o] of orders.entries()) {
      if (o._id === _id) {
        orders.splice(i, 1);
        this.updateOrders({ items: orders });
        break;
      }
    }
    // UPDATE BOARD
    for (const key in this.ordersBoard.lists) {
      if (this.ordersBoard.lists[key]) {
        const index = this.ordersBoard.lists[key].items.findIndex((o) => o._id === _id);
        if (index !== -1) {
          this.ordersBoard.lists[key].items.splice(index, 1);
          break;
        }
      }
    }
  }

  @action setBooking = (data: BookingSchema | null) => {
    this.booking = data;
  }
  @action updateBooking = (data: Partial<BookingSchema>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof BookingSchema];
        if (value !== undefined && this.booking) {
          this.booking[key as keyof BookingSchema] = value;
        }
      }
    }
  }
  @action updateBookingComplete = (item: BookingSchema) => {
    // UPDATE SINGLE
    this.setBooking(item);
    // UPDATE COLLECTION
    const items = [ ...this.bookings.items ];
    for (const [i, o] of items.entries()) {
      if (o._id === item._id) {
        items[i] = item;
        this.updateBookings({ items });
        break;
      }
    }
  }
  @action updateBookings = (data: Partial<GenericQueryCollection<BookingSchema>>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof GenericQueryCollection<BookingSchema>];
        if (value !== undefined && this.bookings) {
          this.bookings[key as keyof GenericQueryCollection<BookingSchema>] = value;
        }
      }
    }
  }
  @action removeBooking = (_id: string) => {
    // UPDATE SINGLE
    if (this.booking && this.booking._id === _id) {
      this.setOrder(null);
    }
    // UPDATE COLLECTION
    const items = [ ...this.bookings.items ];
    for (const [i, o] of items.entries()) {
      if (o._id === _id) {
        items.splice(i, 1);
        this.updateBookings({ items });
        break;
      }
    }
  }

  @action updateCustomers = (data: Partial<GenericQueryCollection<CustomerSchema>>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof GenericQueryCollection<CustomerSchema>];
        if (value !== undefined && this.customers) {
          this.customers[key as keyof GenericQueryCollection<CustomerSchema>] = value;
        }
      }
    }
  }
  @action setCustomer = (data: CustomerSchema | null) => {
    this.customer = data;
  }
  @action updateCustomer = (data: Partial<CustomerSchema>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof CustomerSchema];
        if (value !== undefined && this.customer) {
          this.customer[key as keyof CustomerSchema] = value;
        }
      }
    }
  }
  @action updateCustomerComplete = (item: CustomerSchema) => {
    // UPDATE SINGLE
    this.setCustomer(item);
    // UPDATE COLLECTION
    const items = [ ...this.customers.items ];
    for (const [i, o] of items.entries()) {
      if (o._id === item._id) {
        items[i] = item;
        this.updateCustomers({ items });
        break;
      }
    }
  }
  @action removeCustomer = (_id: string) => {
    // UPDATE SINGLE
    if (this.customer && this.customer._id === _id) {
      this.setCustomer(null);
    }
    // UPDATE COLLECTION
    const items = [ ...this.customers.items ];
    for (const [i, o] of items.entries()) {
      if (o._id === _id) {
        items.splice(i, 1);
        this.updateCustomers({ items });
        break;
      }
    }
  }

  @action windowResize = () => {
    const width = window.innerWidth;
    const breakpoint = CoreUtils.ui.breakpoint(width);
    this.view.screen_width = width;
    this.view.breakpoint = breakpoint;
  }
  @action windowScroll = () => {
    try {
      const h1 = window.pageYOffset;
      const h2 = document.documentElement ? document.documentElement.scrollTop : 0;
      const h3 = document.body.scrollTop;
      this.view.scroll_top = Math.max(h1, h2, h3);
    }
    catch (e) {
      logger.captureException(e, "SCROLL FUNCTION ERROR");
    }
  }

}
